import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GitBranch, Plus } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useChangeRequests, useSearchChangeRequests } from "../api/change-requests-hooks";
import { NewChangeRequestDialog } from "./new-change-request-dialog";

type CrLinkPickerProps = {
  projectId: string;
  workspaceSlug: string;
  projectSlug: string;
  /** Controlled by the owning control: the picker has no trigger of its own. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (crId: string) => void;
  /**
   * The control the popover is positioned against.
   */
  children: ReactNode;
  /**
   * Whether `children` is itself the toggle. The unlinked "Link CR" button is,
   * so it needs Radix's trigger (an anchor alone counts as "outside", and a
   * second click would close then immediately reopen the popover). The linked
   * split control is not: it is only an anchor, opened from its dropdown menu,
   * because its primary segment must stay a plain navigation link.
   */
  asTrigger?: boolean;
  align?: "start" | "end";
};

/**
 * CR search/list/create picker shared by every linked-CR surface (CR-149 D6).
 * Only active, non-archived CRs of the current project are offered as targets —
 * that comes from `changeRequestsApi.listChangeRequestsForProject` /
 * `searchChangeRequests`, which both filter `deleted_at`.
 */
export function CrLinkPicker({
  projectId,
  workspaceSlug,
  projectSlug,
  open,
  onOpenChange,
  onSelect,
  children,
  asTrigger = false,
  align = "start",
}: CrLinkPickerProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { data: allCrs } = useChangeRequests(projectId);
  const { data: searchResults } = useSearchChangeRequests(query, projectId);

  const displayedCrs = query.length > 0 ? (searchResults ?? []) : (allCrs ?? []);

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setQuery("");
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {asTrigger ? (
          <PopoverTrigger asChild>{children}</PopoverTrigger>
        ) : (
          <PopoverAnchor asChild>{children}</PopoverAnchor>
        )}
        <PopoverContent className="w-72 p-0" align={align}>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search change requests..."
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>
                <p>No change requests found.</p>
              </CommandEmpty>
              <CommandGroup>
                {displayedCrs.map((cr) => (
                  <CommandItem
                    key={cr.id}
                    value={cr.id}
                    onSelect={() => {
                      handleOpenChange(false);
                      onSelect(cr.id);
                    }}
                  >
                    <GitBranch className="text-accent-foreground h-4 w-4 shrink-0" />
                    <span className="text-muted-foreground shrink-0 text-xs">
                      CR-{cr.sequential_id}
                    </span>
                    <span className="truncate">{cr.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    handleOpenChange(false);
                    setCreateDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  Create new change request
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Creating a CR navigates to it; CR-149 does not auto-link the new CR. */}
      <NewChangeRequestDialog
        projectId={projectId}
        workspaceSlug={workspaceSlug}
        projectSlug={projectSlug}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  );
}
